import { useEffect, useMemo, useState } from 'react';

import useGetRelativityFieldMapping from '@/api/queries/useGetRelativityFieldMapping';
import type { ConfiguredWorkspace, FieldMappingResponse } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { cn } from '@/new/lib/utils';

interface CopyFieldMappingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaces: ConfiguredWorkspace[];
  onCopy: (sourceMapping: FieldMappingResponse) => void;
}

export function CopyFieldMappingsDialog({ open, onOpenChange, workspaces, onCopy }: CopyFieldMappingsDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);

  // Reset state whenever the dialog is opened so a stale pick/search doesn't linger.
  useEffect(() => {
    if (open) {
      setSelectedSourceId(null);
      setSearchQuery('');
    }
  }, [open]);

  const filteredWorkspaces = useMemo(
    () =>
      workspaces.filter((w) =>
        (w.workspaceName ?? `Workspace ${w.workspaceId}`).toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [workspaces, searchQuery],
  );

  const { data: sourceMapping, isLoading: isLoadingMapping } = useGetRelativityFieldMapping({
    workspaceId: selectedSourceId,
    enabled: open && selectedSourceId !== null,
  });

  const handleConfirm = () => {
    if (!sourceMapping) return;
    onCopy(sourceMapping);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Icon className="lucide--clipboard-paste" />
              <span>Copy field mappings</span>
            </div>
          </DialogTitle>
          <DialogDescription>
            Copy an existing workspace's field mappings into this one. Fields are matched by name and type. Fields not
            found in this workspace are ignored.
          </DialogDescription>
        </DialogHeader>

        <div className="-m-2 -mx-3">
          <SearchInput
            placeholder={`Search ${filteredWorkspaces.length} configured ${filteredWorkspaces.length === 1 ? 'workspace' : 'workspaces'}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="flex h-[45vh] flex-col overflow-y-auto px-1 pt-2">
            {filteredWorkspaces.length === 0 && (
              <p className="my-12 text-center text-gray-400">
                {searchQuery ? 'No matching workspaces.' : 'No configured workspaces to copy from.'}
              </p>
            )}

            {filteredWorkspaces.map((workspace) => {
              const isSelected = selectedSourceId === workspace.workspaceId;
              return (
                <div key={workspace.workspaceId} className="group">
                  <button
                    type="button"
                    onClick={() => setSelectedSourceId(workspace.workspaceId)}
                    className={cn(
                      'my-1 flex w-full cursor-pointer items-center justify-between gap-3 rounded-md p-2.5 px-3 text-left duration-150',
                      isSelected ? 'bg-teal-50/50 ring-1 ring-teal-400' : 'hover:bg-gray-50',
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className={cn('truncate font-medium', isSelected ? 'text-teal-900' : 'text-gray-700')}>
                        {workspace.workspaceName ?? `Workspace ${workspace.workspaceId}`}
                      </span>
                      <span className="flex-shrink-0 font-mono text-gray-400">{workspace.workspaceId}</span>
                    </div>
                    <span className="flex-shrink-0 text-gray-500">
                      {workspace.fieldCount} field{workspace.fieldCount === 1 ? '' : 's'}
                    </span>
                  </button>
                  <hr className="mx-1 my-px group-last:opacity-0" />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={selectedSourceId === null || isLoadingMapping || !sourceMapping}
          >
            {isLoadingMapping ? (
              <>
                <Icon className="lucide--loader animate-spin ease-in-out" />
                Loading...
              </>
            ) : (
              <>
                <Icon className="lucide--clipboard-paste" />
                Copy mappings
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
